/**
 * ImportResultadoContagemModal - Modal for Resultado de Contagem Excel import (Epic 53)
 *
 * Imports Excel file with 6 columns to update quantidade_contada on existing records
 * Integrates with POST /api/contagens/{job_id}/importar-resultado
 */
import React, { useState } from 'react';
import { X, Upload, FileText, AlertCircle, Info, ChevronDown, ChevronUp, ClipboardPaste } from 'lucide-react';
import contagemService, { ImportResultadoResponse } from '../../services/contagemService';
import { tsvToXlsxFile, parseTsv } from '../../utils/pasteToXlsx';

const MAX_ERRORS_SHOWN = 20;

interface ImportResultadoContagemModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: number;
  onImportSuccess: (data: ImportResultadoResponse) => void;
}

// Column definitions for the tooltip
const RESULTADO_COLUMNS = [
  { col: 'A', name: 'Código Cliente', required: true, description: 'CNPJ do cliente' },
  { col: 'B', name: 'Código', required: true, description: 'Código do produto' },
  { col: 'C', name: 'Descrição', required: false, description: 'Descrição do produto' },
  { col: 'D', name: 'Lote', required: true, description: 'Número do lote' },
  { col: 'E', name: 'Validade', required: true, description: 'DD/MM/YYYY' },
  { col: 'F', name: 'Qtde (Contada)', required: true, description: 'Quantidade contada' },
];

function ImportResultadoResult({ result, onClose }: { result: ImportResultadoResponse; onClose: () => void }) {
  const hasErrors = result.total_erros > 0 || result.total_nao_encontrados > 0;
  const hasSuccess = result.total_atualizados > 0;
  const naoEncontrados = result.itens_nao_encontrados || [];
  const erros = result.erros || [];

  const bgClass = !hasSuccess
    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    : hasErrors
    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';

  return (
    <div className="space-y-4">
      <div className={`rounded-lg p-4 border ${bgClass}`}>
        {/* Success line */}
        {result.total_atualizados > 0 && (
          <p className="text-sm text-green-700 dark:text-green-300 mb-1">
            ✅ {result.total_atualizados} itens atualizados com sucesso
          </p>
        )}

        {/* Itens não encontrados */}
        {naoEncontrados.length > 0 && (
          <div className="mt-2">
            <p className="text-sm text-amber-700 dark:text-amber-400 mb-1">
              ⚠️ {result.total_nao_encontrados} itens não encontrados na ordem:
            </p>
            <ul className="text-xs text-amber-600 dark:text-amber-400 list-disc list-inside max-h-40 overflow-y-auto space-y-0.5">
              {naoEncontrados.slice(0, MAX_ERRORS_SHOWN).map((e, i) => (
                <li key={i}>
                  Linha {e.linha}: [{e.codigo}] Lote: {e.lote} - {e.erro}
                </li>
              ))}
              {naoEncontrados.length > MAX_ERRORS_SHOWN && (
                <li className="text-gray-500 dark:text-gray-400">
                  ... e mais {naoEncontrados.length - MAX_ERRORS_SHOWN} item(ns)
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Erros de validação — Story 2.13: render defensivo aceita
            string OR objeto. Backend legado podia retornar strings tipo
            "Linha X: msg", resultando em "Linha : []" vazio. */}
        {erros.length > 0 && (
          <div className="mt-2">
            <p className="text-sm text-red-700 dark:text-red-300 mb-1">
              ❌ {result.total_erros} erro(s) encontrado(s):
            </p>
            <ul className="text-xs text-red-600 dark:text-red-400 list-disc list-inside max-h-40 overflow-y-auto space-y-0.5">
              {erros.slice(0, MAX_ERRORS_SHOWN).map((e: unknown, i) => {
                const isString = typeof e === 'string';
                let linha: string | number = '?';
                let codigo = '';
                let mensagem = '';
                if (isString) {
                  const m = (e as string).match(/^Linha\s+(\d+):\s*(.+)$/);
                  if (m) {
                    linha = parseInt(m[1], 10);
                    mensagem = m[2].trim();
                  } else {
                    mensagem = e as string;
                  }
                } else {
                  const obj = e as { linha?: number | string; codigo?: string; erro?: string };
                  linha = obj.linha ?? '?';
                  codigo = obj.codigo ?? '';
                  mensagem = obj.erro ?? 'Erro não especificado';
                }
                return (
                  <li key={i}>
                    <span className="font-semibold">Linha {linha}:</span>{' '}
                    {codigo && <code className="bg-red-50 dark:bg-red-900/40 px-1 rounded">{codigo}</code>}{' '}
                    {mensagem}
                  </li>
                );
              })}
              {erros.length > MAX_ERRORS_SHOWN && (
                <li className="text-gray-500 dark:text-gray-400">
                  ... e mais {erros.length - MAX_ERRORS_SHOWN} erro(s)
                </li>
              )}
            </ul>
          </div>
        )}

        {/* All success */}
        {!hasErrors && hasSuccess && (
          <p className="text-sm text-green-700 dark:text-green-300 mt-1">
            Todas as {result.total_linhas} linhas processadas sem erros.
          </p>
        )}
      </div>

      {/* Close button */}
      <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onClose}
          className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium"
        >
          Fechar
        </button>
      </div>
    </div>
  );
}

export default function ImportResultadoContagemModal({
  isOpen,
  onClose,
  jobId,
  onImportSuccess,
}: ImportResultadoContagemModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [importResult, setImportResult] = useState<ImportResultadoResponse | null>(null);
  // #15: modo arquivo OU colar do Excel (TSV)
  const [mode, setMode] = useState<'arquivo' | 'colar'>('arquivo');
  const [pastedText, setPastedText] = useState('');
  const pastedRows = parseTsv(pastedText).length;

  if (!isOpen) return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setError('Arquivo deve ser Excel (.xlsx ou .xls)');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    // #15: resolve arquivo a partir do modo (arquivo OU colado→xlsx)
    let fileToSend: File | null = selectedFile;
    if (mode === 'colar') {
      fileToSend = tsvToXlsxFile(pastedText, `resultado_${jobId}.xlsx`);
      if (!fileToSend) {
        setError('Cole os dados do Excel primeiro');
        return;
      }
    } else if (!selectedFile) {
      setError('Selecione um arquivo primeiro');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const response = await contagemService.importarResultadoContagem(jobId, fileToSend as File);

      console.log('Import Resultado success:', response);
      setImportResult(response);
      onImportSuccess(response);
    } catch (err: any) {
      console.error('Import Resultado error:', err);
      setError(
        err.response?.data?.error ||
          err.message ||
          'Erro ao fazer upload do arquivo'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPastedText('');
    setMode('arquivo');
    setError(null);
    setImportResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full">
        {/* Header - Blue color */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Upload size={24} />
            Importar Resultado de Contagem
          </h2>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Import Result */}
          {importResult ? (
            <ImportResultadoResult result={importResult} onClose={handleClose} />
          ) : (
          <>
          {/* #15: alternar Arquivo / Colar do Excel */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('arquivo')}
              disabled={uploading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${mode === 'arquivo' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <Upload size={16} /> Arquivo Excel
            </button>
            <button
              type="button"
              onClick={() => setMode('colar')}
              disabled={uploading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${mode === 'colar' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <ClipboardPaste size={16} /> Colar do Excel
            </button>
          </div>

          {mode === 'arquivo' ? (
          /* File Input */
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Selecione o arquivo Excel:
            </label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={uploading}
                className="flex-1 text-sm text-gray-600 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-900/20 file:text-blue-700 dark:file:text-blue-400 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/30 disabled:opacity-50 cursor-pointer"
              />
              {selectedFile && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <FileText size={16} />
                  <span className="font-medium">{selectedFile.name}</span>
                  <span className="text-xs text-gray-500">
                    ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </span>
                </div>
              )}
            </div>
          </div>
          ) : (
          /* #15: Colar do Excel (TSV) */
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Selecione as colunas no Excel, copie (Ctrl+C) e cole abaixo (Ctrl+V):
            </label>
            <textarea
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              disabled={uploading}
              rows={10}
              placeholder="Cole aqui as células copiadas do Excel..."
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            {pastedRows > 0 && (
              <p className="text-xs text-gray-500 mt-1">{pastedRows} linha(s) detectada(s) (a 1ª, cabeçalho, é ignorada).</p>
            )}
          </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-red-800 dark:text-red-400">
                  Erro na Importação
                </h4>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                  Enviando arquivo e atualizando dados...
                </span>
              </div>
            </div>
          )}

          {/* Collapsible Info Panel */}
          {showInfo && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg animate-in slide-in-from-top-2 duration-200">
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                Formato Esperado - 6 Colunas
              </h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                Este arquivo importa os <strong>resultados após a contagem física</strong>.
                <span className="text-amber-600 dark:text-amber-400 font-medium"> Requer itens previamente importados!</span>
              </p>
              <div className="grid grid-cols-2 gap-2">
                {RESULTADO_COLUMNS.map((col) => (
                  <div
                    key={col.col}
                    className="flex items-center text-sm text-gray-700 dark:text-gray-300"
                  >
                    <span className="font-mono bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs mr-2">
                      {col.col}
                    </span>
                    <span className={col.required ? 'font-medium' : ''}>
                      {col.name}
                      {col.required && <span className="text-red-500 ml-0.5">*</span>}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-3 italic">
                * Campos obrigatórios. A primeira linha (cabeçalho) será ignorada.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleClose}
              disabled={uploading}
              className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={() => setShowInfo(!showInfo)}
              className={`flex items-center gap-2 px-4 py-2.5 border-2 rounded-lg transition font-medium ${
                showInfo
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
              }`}
              title="Ver formato esperado"
            >
              <Info size={18} />
              {showInfo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading || (mode === 'arquivo' ? !selectedFile : pastedRows === 0)}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={18} />
              {uploading ? 'Enviando...' : 'Importar'}
            </button>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
